import React, { useEffect, useState, useMemo } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import SearchableAssetResults from '../../ui/inputs/SearchableAssetResults';
import { BanknoteArrowUp, X } from 'lucide-react';
import { useToast } from '../../ui/toast/ToastContext';
import SecondaryButton from '../../ui/buttons/SecondaryButton';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import CustomSelect from '../../ui/selects/CustomSelect';

const AddWalletAssetModal = ({ onClose, onSuccess, walletType }) => {
  const { addToast } = useToast();

  const assetOptions = useMemo(() => {
    if (walletType === 'pension' || walletType === 'PREVIDÊNCIA') {
      return [{ value: 'PensionFund', label: 'Fundo de Previdência' }];
    }

    return [
      { value: 'InvestmentFund', label: 'Fundo de Investimento' },
      { value: 'Equity_fii', label: 'FII' },
      { value: 'Equity_stock', label: 'Ação' },
    ];
  }, [walletType]);

  const [form, setForm] = useState({
    assetType: assetOptions[0]?.value || '',
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'assetType') {
      setSearchTerm('');
      setSelectedAsset(null);
      setSearchResults([]);
    }
  };

  useEffect(() => {
    if (searchTerm.length < 3 || selectedAsset) {
      setSearchResults([]);
      return;
    }

    const controller = new AbortController();

    const [type, kind] = form.assetType.split('_');
    const kindQuery = kind ? `&kind=${kind}` : '';

    fetch(
      `/app/analyst/wallet_configs/wallet_assets/search?q=${encodeURIComponent(searchTerm)}&type=${type}${kindQuery}`,
      { signal: controller.signal }
    )
      .then((res) => res.json())
      .then((data) => setSearchResults(camelizeKeys(data)))
      .catch((err) => console.warn('Busca ignorada:', err));

    return () => controller.abort();
  }, [searchTerm, form.assetType, selectedAsset]);

  const handleSelectResult = (asset) => {
    setSelectedAsset(asset);
    setSearchTerm(asset.name);
    setSearchResults([]);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedAsset(null);
    setSearchResults([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedAsset) {
      addToast({ type: 'error', message: 'Por favor, busque e selecione um ativo na lista.' });
      return;
    }

    const [baseType, baseKind] = form.assetType.split('_');

    onSuccess({
      assetType: baseType, // Ex: 'Equity' ou 'InvestmentFund'
      assetKind: baseKind, // Ex: 'stock' ou 'fii'
      assetId: selectedAsset.id,
      assetName: selectedAsset.name,
      assetCge: selectedAsset.cge,
      assetCnpj: selectedAsset.cnpj,
      assetDisplayName: selectedAsset.displayName,
      assetTicker: selectedAsset.ticker,
      assetTSector: selectedAsset.tSector,
      allocationPercentage: 0,
      _destroy: false,
    });
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <BanknoteArrowUp size={24} className="text-tealMedium" strokeWidth={1.25} />
            Buscar Ativo
          </div>
          <button type="button" onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 relative">
          <CustomSelect
            name="assetType"
            label="Classe do Ativo"
            value={form.assetType}
            onChange={handleChange}
            required
            options={assetOptions}
          />

          <SearchableAssetResults
            name="searchTerm"
            label="Nome do ativo"
            placeholder="Procurar ativo..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelectedAsset(null);
            }}
            onClear={handleClearSearch}
            autoFocus
            results={searchResults}
            onSelectResult={handleSelectResult}
            renderResult={(asset) => (
              <>
                <span className="font-semibold block">{asset.name}</span>
                <span className="text-xs text-blueSteel">{asset.documentNumber || 'Sem CNPJ'}</span>
              </>
            )}
          />

          <div className="flex justify-end items-center mt-4">
            <div className="flex justify-between gap-2">
              <SecondaryButton type="button" onClick={onClose}>
                CANCELAR
              </SecondaryButton>
              <Button type="submit">ADICIONAR À CARTEIRA</Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddWalletAssetModal;
